import { useState, useRef } from 'react'
import { PeriodSelection, PeriodSelector, useDefaultDateRange } from '~/components/PeriodSelector'
import { DateRange } from '~/types'
import { StationsConsumptionChart, StationsConsumptionChartProps } from './StationsConsumptionChart'
import { SectionCard } from '~/components/SectionCard'
import ResolutionSelector from '~/components/ResolutionSelector'
import { Resolution } from '@willie/schemas'
import { ConsumptionChartTypePicker } from './ChartTypePicker'
import { ChartType } from '../schemas'
import { ViewOptionsPopover } from '~/features/dashboarding/components/ViewOptionsPopover'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import { Flex } from '@radix-ui/themes'

import { useChartExportCSV, ChartExportHandle } from '../hooks/useChartExportCSV'
import { useChartExportImage } from '../hooks/useChartExportImage'
import { ChartSectionMoreOptionsMenu } from './MoreOptionsDropdownMenu'
import { useTranslation } from 'react-i18next'

type Props = Omit<StationsConsumptionChartProps, 'resolution'> & {
  siteID?: string
  metricID?: string
}

const StationConsumptionChartSection = ({ title, ...props }: Props) => {
  const { t } = useTranslation(['sites'])
  const [resolution, setResolution] = useState<Resolution>('day')
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>(!props.startDate ? 'week' : 'custom')
  const [chartType, setChartType] = useState<ChartType>(props.chartType || 'area')
  const [dateRange, setDateRange] = useState<DateRange>(
    !props.startDate || !props.endDate
      ? useDefaultDateRange('week')
      : {
          startDate: props.startDate,
          endDate: props.endDate
        }
  )
  const chartRef = useRef<ChartExportHandle>(null as any)
  const exportFilename = `${props.metricID ? 'metric_' + props.metricID.slice(0, 8) : 'consumption'}_${dateRange.startDate.toISODate()}_${dateRange.endDate.toISODate()}.csv`
  const handleExportCSV = useChartExportCSV(chartRef, exportFilename)

  const imageExportFilename = `${props.metricID ? 'metric_' + props.metricID.slice(0, 8) : 'consumption'}_${dateRange.startDate.toISODate()}_${dateRange.endDate.toISODate()}.png`
  const { ref: imageExportRef, handleExportImage, isExporting } = useChartExportImage(imageExportFilename)

  const handlePeriodChange = (newDateRange: DateRange, newPeriod: PeriodSelection) => {
    setDateRange(newDateRange)
    setPeriodSelection(newPeriod)
  }

  return (
    <SectionCard
      title={title}
      innerRef={imageExportRef}
      secondaryActions={
        <Flex gap="2" align={'end'} className="no-export">
          <ViewOptionsPopover>
            <FieldWrapper label={'Période'}>
              <PeriodSelector
                size={'2'}
                periodSelection={periodSelection}
                dateRange={dateRange}
                onPeriodChange={handlePeriodChange}
              />
            </FieldWrapper>
            <FieldWrapper label={'Résolution'}>
              <ResolutionSelector size={'2'} defaultResolution={resolution} onChange={setResolution} />
            </FieldWrapper>
            <FieldWrapper label={'Type de graphique'}>
              <ConsumptionChartTypePicker
                selectRootProps={{ size: '2' }}
                defaultType={props.chartType}
                onTypeChange={(type) => setChartType(type)}
              />
            </FieldWrapper>
          </ViewOptionsPopover>
          <ChartSectionMoreOptionsMenu
            handleExportCSV={handleExportCSV}
            handleExportImage={handleExportImage}
            isExporting={isExporting}
            linkTo={props.siteID && props.metricID ? '/sites/$siteID/config/metrics/$metricID' : undefined}
            linkParams={{ siteID: props.siteID, metricID: props.metricID }}
            linkText={t('metrics.chart.settings')}
          />
        </Flex>
      }
    >
      <StationsConsumptionChart
        ref={chartRef}
        title={''}
        {...props}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        resolution={resolution}
        chartType={chartType}
      />
    </SectionCard>
  )
}

export { StationConsumptionChartSection }
